import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  Compass,
  Layers3,
  Lock,
  MapPinned,
  MessageSquareQuote,
  Network,
  Search,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
  Eye,
  Presentation,
} from 'lucide-react';

import logo from '../assets/images/candid-i-logo.png';

const trustItems = [
  { label: 'Markets covered', value: '50+ global talent markets' },
  { label: 'Search style', value: 'Confidential, research-led' },
  { label: 'Audience', value: 'Employers and candidates' },
  { label: 'Delivery model', value: 'Human-led + AI-assisted' },
];

const employerTrust = [
  { label: 'Mandate clarity', value: 'Define the role before the search' },
  { label: 'Market visibility', value: 'Know where talent sits' },
  { label: 'Decision support', value: 'Shortlist with conviction' },
];

const candidateTrust = [
  { label: 'Relevant access', value: 'Opportunities aligned with your goals' },
  { label: 'Career intelligence', value: 'Market, compensation and skills context' },
  { label: 'Your control', value: 'Permission before every introduction' },
];

const talentNetworkPrinciples = [
  { label: 'Specialist markets', value: 'Commodities, financial markets and technology' },
  { label: 'Private by default', value: 'Permission-led profile introductions' },
  { label: 'Career intelligence', value: 'Opportunities, skills and market context' },
  { label: 'Delivery model', value: 'AI-assisted · Human-supported' },
];

const employerProblems = [
  {
    title: 'Market complexity',
    body: 'The best candidates are often concentrated in a small set of firms, making broad outreach slow and inefficient.',
    icon: Search,
  },
  {
    title: 'Leadership scarcity',
    body: 'Hybrid commercial and operational profiles are rare, so role definition and market positioning matter more than ever.',
    icon: Layers3,
  },
  {
    title: 'Hiring risk',
    body: 'A weak brief or a generic process increases drop-off, delays, and misalignment across the shortlist.',
    icon: ShieldCheck,
  },
];

const employerPillars = [
  {
    title: 'Executive search',
    body: 'Focused, premium search support built around role clarity, market mapping, and direct candidate engagement.',
    icon: BriefcaseBusiness,
  },
  {
    title: 'Market intelligence',
    body: 'Talent concentration, movement signals, compensation pressure, and market context that sharpen the brief.',
    icon: BarChart3,
  },
  {
    title: 'Talent mapping',
    body: 'Understand who sits where, how the market clusters, and which employers are likely to produce the right profiles.',
    icon: MapPinned,
  },
  {
    title: 'Advisory support',
    body: 'Briefing calls, shortlist review, and calibrated guidance to reduce decision risk at every stage.',
    icon: Presentation,
  },
];

const employerCapabilities = [
  {
    title: 'Executive Search',
    body: 'Senior mandates delivered with structure, pace, and market discipline.',
    icon: BriefcaseBusiness,
  },
  { title: 'Talent Mapping', body: 'Reveal the firms, desks, and people most likely to matter.', icon: Network },
  { title: 'Market Intelligence', body: 'Editorial insight for employers who need context, not noise.', icon: BarChart3 },
  { title: 'Compensation Signals', body: 'Understand how package pressure is shaping candidate movement.', icon: TrendingUp },
  { title: 'Succession Support', body: 'Identify the continuity risk before it becomes visible in the market.', icon: Layers3 },
  { title: 'Briefing Support', body: 'Turn a difficult mandate into a sharper market conversation.', icon: Presentation },
];

const employerProofStats = [
  { label: 'Time to shortlist', value: 'Days, not weeks' },
  { label: 'Market visibility', value: 'Concentrated and current' },
  { label: 'Search style', value: 'Research-led precision' },
  { label: 'Confidence gained', value: 'Better mandate fit' },
];

const employerProcess = [
  { title: 'Briefing', body: 'We clarify the mandate, scope, must-haves, and the market reality around the role.' },
  { title: 'Market scan', body: 'We identify where relevant people sit and what move triggers are likely to matter.' },
  { title: 'Candidate evaluation', body: 'We assess candidates by fit, market relevance, and engagement likelihood.' },
  { title: 'Shortlist presentation', body: 'We surface the right people with context, not just names.' },
  { title: 'Selection support', body: 'We help you navigate the final decision with confidence and commercial context.' },
  { title: 'Placement follow-through', body: 'We stay close to the move so momentum is not lost after the offer.' },
];

const employerFaq = [
  {
    question: 'What kinds of roles do you handle?',
    answer: 'Senior and specialist mandates where market understanding, candidate quality, and process discipline matter.',
  },
  {
    question: 'How fast can you produce a shortlist?',
    answer: 'Speed depends on role complexity, but the goal is always to move from briefing to live market insight quickly.',
  },
  {
    question: 'Do you work retained or advisory?',
    answer: 'We support both search and advisory-style conversations, depending on the mandate and client need.',
  },
  {
    question: 'Can you support confidential searches?',
    answer: 'Yes. Confidentiality is a core part of the process and is handled with selective outreach and controlled sharing.',
  },
  {
    question: 'Do you provide market intelligence without a live search?',
    answer: 'Yes. Clients can engage us for briefing-led insight, market mapping, and context even before a search begins.',
  },
];

const networkBenefits = [
  {
    title: 'Your profile',
    body: 'Keep your experience, goals and preferences current.',
    icon: Users,
  },
  {
    title: 'Opportunity matching',
    body: 'Explore roles aligned with your direction.',
    icon: Target,
  },
  {
    title: 'Market intelligence',
    body: 'Understand demand, compensation and movement.',
    icon: Eye,
  },
  {
    title: 'Your control',
    body: 'Decide when your profile is introduced.',
    icon: Lock,
  },
];

const careerAgentCapabilities = [
  {
    id: 'opportunity-manager',
    title: 'Opportunity Manager',
    summary: 'Find opportunities aligned with your direction.',
    description:
      'Discover, compare and manage roles against your experience, goals and preferences.',
    features: ['Opportunity discovery', 'Role evaluation', 'Application tracking', 'Assisted applications'],
    status: 'In development',
    icon: BriefcaseBusiness,
  },
  {
    id: 'career-coach',
    title: 'Career Coach',
    summary: 'Plan your career with better context.',
    description:
      'Clarify your goals and prepare for interviews and important career decisions.',
    features: ['Career goal planning', 'Pathway exploration', 'Interview preparation', 'Decision support'],
    status: 'In development',
    icon: Compass,
  },
  {
    id: 'skills-navigator',
    title: 'Skills Navigator',
    summary: 'Build the capabilities your next role requires.',
    description:
      'Understand your skills gaps and access focused development recommendations.',
    features: ['Skills profile', 'Role-gap analysis', 'Development tracking', 'Curated learning'],
    status: 'In development',
    icon: BarChart3,
  },
  {
    id: 'reputation-network',
    title: 'Reputation Network',
    summary: 'Strengthen the network around your career.',
    description:
      'Understand your professional relationships and identify trusted paths to new opportunities.',
    features: ['Network mapping', 'Connection intelligence', 'Relationship development', 'Trusted introductions'],
    status: 'In development',
    icon: Network,
  },
];

const opportunityTypes = [
  {
    title: 'Executive leadership',
    body: 'Board, C-suite and regional appointments.',
    icon: Users,
  },
  {
    title: 'Commercial leadership',
    body: 'Trading, sales, operations and functional roles.',
    icon: BriefcaseBusiness,
  },
  {
    title: 'Specialist roles',
    body: 'Positions requiring deep market expertise.',
    icon: Building2,
  },
  {
    title: 'Growth & market entry',
    body: 'Roles supporting expansion and new capabilities.',
    icon: TrendingUp,
  },
  {
    title: 'Confidential search',
    body: 'Controlled appointments and succession planning.',
    icon: Layers3,
  },
];

const candidateProcess = [
  { title: 'Join', body: 'Create your private profile.' },
  { title: 'Set your direction', body: 'Add your goals, markets and preferences.' },
  { title: 'Activate your Career Agent', body: 'Explore opportunities, skills and relationships.' },
  { title: 'Receive relevant signals', body: 'See intelligence aligned with your profile.' },
  { title: 'Decide', body: 'Choose which conversations to pursue.' },
];

const candidateConfidentialityPoints = [
  'Private by default',
  'No introduction without permission',
  'Update or remove your profile at any time',
];

const candidateSupportItems = [
  {
    title: 'Understand your market',
    body: 'Market position, compensation context and skills demand.',
  },
  {
    title: 'Navigate your next move',
    body: 'Career timing, interview intelligence and decision support.',
  },
];

const candidateProofPoints = [
  'Permission-led',
  'Selective mandates',
  'Clear communication',
  'Specialist context',
];

const candidateFaq = [
  {
    question: 'How do I join the Candid-I Talent Network?',
    answer: 'Start with the join form and share the career information you are comfortable providing. We will confirm the next step with you.',
  },
  {
    question: 'Do I need to be actively looking?',
    answer: 'No. You can join to build career context and explore the market before you are ready to make a move.',
  },
  {
    question: 'Will my profile be visible to employers?',
    answer: 'No. Your Talent Network profile remains private unless you give permission for a specific introduction.',
  },
  {
    question: 'When is my information shared?',
    answer: 'Only after we explain the employer, mandate and context, and you agree to the introduction.',
  },
  {
    question: 'How does ALICE use my information?',
    answer: 'ALICE helps organise your profile, goals, skills and opportunity preferences. People remain responsible for important introductions and advice.',
  },
  {
    question: 'What Career Agent tools will I receive?',
    answer:
      'Career Agent capabilities are in development and will be introduced progressively. The planned workspace includes opportunity management, career guidance, skills intelligence and professional network development.',
  },
  {
    question: 'What types of opportunities do you cover?',
    answer: 'We focus on leadership and specialist appointments across commodities, financial markets and technology.',
  },
  {
    question: 'How often will Candid-I contact me?',
    answer: 'We aim to contact you when there is a relevant opportunity, useful market intelligence or an important profile update.',
  },
  {
    question: 'Can I update or remove my profile?',
    answer: 'Yes. You can update your preferences or ask us to remove your Talent Network profile.',
  },
  {
    question: 'Is there a fee to join?',
    answer: 'There is no fee for professionals to join the Candid-I Talent Network.',
  },
];

function SiteHeader({ ctaLabel, ctaHref }) {
  return (
    <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[88rem] items-center justify-between px-5 py-4 lg:px-8">
        <Link to="/" className="flex items-center gap-3">
          <img src={logo} alt="Candid-I" className="h-8 w-auto" />
        </Link>
        <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
          <Link to="/employers" className="transition hover:text-[#f7911d]">Employers</Link>
          <Link to="/talent" className="transition hover:text-[#f7911d]">Talent Network</Link>
          <Link to="/market-intelligence" className="transition hover:text-[#f7911d]">Market intelligence</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to={ctaHref}
            className="rounded-full bg-[#0f172a] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#111c35]"
          >
            {ctaLabel}
          </Link>
        </div>
      </div>
    </header>
  );
}

function SiteFooter({ ctaLabel, ctaHref }) {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto grid max-w-[88rem] gap-8 px-5 py-10 lg:grid-cols-[1.2fr_0.8fr] lg:px-8">
        <div>
          <img src={logo} alt="Candid-I" className="h-8 w-auto" />
          <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600">
            Premium executive search and market intelligence for employers and candidates who want more context, more discretion, and better decisions.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Link className="rounded-2xl border border-slate-200 p-4 text-sm font-medium text-slate-700 transition hover:border-[#f7911d] hover:text-[#f7911d]" to={ctaHref}>
            {ctaLabel}
          </Link>
          <Link className="rounded-2xl border border-slate-200 p-4 text-sm font-medium text-slate-700 transition hover:border-[#f7911d] hover:text-[#f7911d]" to="/market-intelligence/subscribe">
            Subscribe for updates
          </Link>
        </div>
      </div>
    </footer>
  );
}

function PageHero({
  eyebrow,
  title,
  description,
  primaryCta,
  secondaryCta,
  tertiaryCta,
  trustItems: items,
  media,
  tone = 'dark',
}) {
  const isLight = tone === 'light';

  return (
    <section className={isLight ? 'bg-[#f7f7f4]' : 'bg-[radial-gradient(circle_at_top_left,_#13213f_0%,_#07111f_55%,_#050b15_100%)] text-white'}>
      <div className="mx-auto grid max-w-[88rem] gap-10 px-5 py-16 lg:grid-cols-[1.08fr_0.92fr] lg:px-8 lg:py-20">
        <div className="max-w-3xl">
          <span className={`inline-flex rounded-full border px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] ${isLight ? 'border-[#f7911d]/30 bg-white text-[#f7911d]' : 'border-white/15 bg-white/5 text-[#f7911d]'}`}>
            {eyebrow}
          </span>
          <h1 className={`mt-6 text-4xl font-semibold tracking-tight md:text-6xl ${isLight ? 'text-slate-950' : 'text-white'}`}>
            {title}
          </h1>
          <p className={`mt-6 max-w-2xl text-lg leading-8 md:text-xl ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
            {description}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={primaryCta.href}
              className="inline-flex items-center gap-2 rounded-full bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200/40 transition hover:-translate-y-0.5 hover:bg-[#e77e08]"
            >
              {primaryCta.label}
              <ArrowRight className="h-4 w-4" />
            </Link>
            {secondaryCta ? (
              <Link
                to={secondaryCta.href}
                className={`inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold transition ${
                  isLight
                    ? 'border-slate-200 bg-white text-slate-800 hover:border-[#f7911d] hover:text-[#f7911d]'
                    : 'border-white/15 bg-white/5 text-white hover:border-[#f7911d] hover:text-[#f7911d]'
                }`}
              >
                {secondaryCta.label}
              </Link>
            ) : null}
          </div>
          {tertiaryCta ? (
            <Link
              to={tertiaryCta.href}
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#f7911d] transition hover:text-[#e77e08]"
            >
              {tertiaryCta.label}
              <ArrowRight className="h-4 w-4" />
            </Link>
          ) : null}
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {items.map((item) => (
              <div key={item.label} className={`rounded-2xl border p-4 shadow-sm ${isLight ? 'border-white/70 bg-white/80' : 'border-white/10 bg-white/5'}`}>
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-[#f7911d]">{item.label}</div>
                <div className={`mt-2 text-sm ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 -z-10 rounded-[2rem] bg-gradient-to-br from-[#f7911d]/20 via-white to-slate-100 blur-3xl" />
          {media}
        </div>
      </div>
    </section>
  );
}

function TrustStrip({ items }) {
  return (
    <section className="bg-white">
      <div className="mx-auto grid max-w-[88rem] gap-4 px-5 py-4 sm:grid-cols-2 xl:grid-cols-4 lg:px-8">
        {items.map((item) => (
          <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#f7911d]">{item.label}</div>
            <div className="mt-2 text-sm font-medium text-slate-700">{item.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, description, align = 'left' }) {
  return (
    <div className={align === 'center' ? 'mx-auto max-w-3xl text-center' : ''}>
      {eyebrow ? (
        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">{eyebrow}</div>
      ) : null}
      <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl">{title}</h2>
      {description ? <p className="mt-4 text-base leading-7 text-slate-600 md:text-lg">{description}</p> : null}
    </div>
  );
}

function FeatureGrid({ items }) {
  return (
    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div key={item.title} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
            {Icon ? (
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-[#f7911d]">
                <Icon className="h-5 w-5" />
              </div>
            ) : null}
            <h3 className="mt-5 text-xl font-semibold text-slate-950">{item.title}</h3>
            <p className="mt-3 text-sm leading-7 text-slate-600">{item.body}</p>
          </div>
        );
      })}
    </div>
  );
}

function ProofGrid({ items }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => (
        <div key={item.label} className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#f7911d]">{item.label}</div>
          <div className="mt-3 text-2xl font-semibold text-slate-950">{item.value}</div>
          {item.body ? <p className="mt-2 text-sm leading-7 text-slate-600">{item.body}</p> : null}
        </div>
      ))}
    </div>
  );
}

function Timeline({ steps, dark = false, connected = false }) {
  return (
    <div className={`relative grid gap-4 ${connected ? 'md:grid-cols-5 md:gap-3' : 'lg:grid-cols-3'}`}>
      {connected ? (
        <>
          <div className="absolute bottom-8 left-[10%] right-[10%] top-8 hidden h-px bg-slate-200 md:block" aria-hidden="true" />
          <div className="absolute bottom-8 left-5 top-8 w-px bg-slate-200 md:hidden" aria-hidden="true" />
        </>
      ) : null}
      {steps.map((step, index) => (
        <div
          key={step.title}
          className={`relative rounded-[2rem] border p-6 shadow-sm ${
            dark ? 'border-white/10 bg-white/5 text-white' : 'border-slate-200 bg-white text-slate-950'
          }`}
        >
          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">
            {`0${index + 1}`}
          </div>
          <h3 className="mt-4 text-xl font-semibold">{step.title}</h3>
          <p className={`mt-3 text-sm leading-7 ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{step.body}</p>
        </div>
      ))}
    </div>
  );
}

function CareerCapabilityCard({ capability }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = capability.icon;
  const panelId = `${capability.id}-details`;

  return (
    <article className="flex h-full flex-col rounded-[2rem] border border-slate-200 bg-[#f8fafc] p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-[#f7911d]">
          <Icon className="h-5 w-5" />
        </div>
        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
          Status: {capability.status}
        </span>
      </div>
      <h3 className="mt-5 text-xl font-semibold text-slate-950">{capability.title}</h3>
      <p className="mt-2 text-sm font-semibold text-[#d96f00]">{capability.summary}</p>
      <p className="mt-3 text-sm leading-7 text-slate-600">{capability.description}</p>
      <div className="mt-auto pt-5">
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={() => setIsExpanded((current) => !current)}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#f7911d] hover:text-[#d96f00] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f7911d]"
        >
          {isExpanded ? 'Hide capability' : 'Explore capability'}
          <ArrowRight className={`h-4 w-4 transition-transform motion-reduce:transition-none ${isExpanded ? 'rotate-90' : ''}`} />
        </button>
        {isExpanded ? (
          <ul id={panelId} className="mt-4 grid gap-2 border-t border-slate-200 pt-4 sm:grid-cols-2">
            {capability.features.map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-[#f7911d]" />
                {feature}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </article>
  );
}

function FaqAccordion({ items }) {
  return (
    <div className="grid gap-4">
      {items.map((item) => (
        <details key={item.question} className="group rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-base font-semibold text-slate-950">
            <span>{item.question}</span>
            <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-[#f7911d] group-open:bg-[#f7911d] group-open:text-white">
              FAQ
            </span>
          </summary>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">{item.answer}</p>
        </details>
      ))}
    </div>
  );
}

function CTASection({ title, description, primaryCta, secondaryCta, tertiaryCta, variant = 'dark' }) {
  const dark = variant === 'dark';

  return (
    <section className={dark ? 'bg-[#07111f] text-white' : 'bg-[#f7f7f4] text-slate-950'}>
      <div className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
        <div className={`rounded-[2.5rem] p-8 shadow-sm lg:p-10 ${dark ? 'border border-white/10 bg-white/5' : 'border border-slate-200 bg-white'}`}>
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">Final CTA</div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">{title}</h2>
              <p className={`mt-4 max-w-2xl text-base leading-7 md:text-lg ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{description}</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to={primaryCta.href}
                className="inline-flex items-center gap-2 rounded-full bg-[#f7911d] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#e77e08]"
              >
                {primaryCta.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
              {secondaryCta ? (
                <Link
                  to={secondaryCta.href}
                  className={`inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold transition ${
                    dark
                      ? 'border-white/15 bg-white/5 text-white hover:border-[#f7911d] hover:text-[#f7911d]'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-[#f7911d] hover:text-[#f7911d]'
                  }`}
                >
                  {secondaryCta.label}
                </Link>
              ) : null}
              {tertiaryCta ? (
                <Link
                  to={tertiaryCta.href}
                  className="inline-flex items-center gap-2 px-2 py-3 text-sm font-semibold text-[#f7911d] transition hover:text-[#e77e08]"
                >
                  {tertiaryCta.label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EmployerHeroMedia() {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b1528] shadow-[0_30px_80px_rgba(15,23,42,0.18)]">
      <div className="border-b border-white/10 px-6 py-4">
        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">Executive insight</div>
        <h3 className="mt-2 text-2xl font-semibold text-white">A sharper read on the market</h3>
      </div>
      <div className="grid gap-4 p-6 lg:grid-cols-[0.95fr_1.05fr]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Search speed</div>
            <div className="mt-2 text-2xl font-semibold text-white">Faster mandate framing</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Fit signal</div>
            <div className="mt-2 text-2xl font-semibold text-white">Clearer shortlist confidence</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Market context</div>
            <div className="mt-2 text-2xl font-semibold text-white">Less guesswork, more signal</div>
          </div>
        </div>
        <div className="overflow-hidden rounded-[1.75rem] border border-white/10 bg-slate-900">
          <img src="/business-impact-image.png" alt="Executive market intelligence visual" className="h-full w-full object-cover" />
        </div>
      </div>
    </div>
  );
}

function CandidateHeroMedia() {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.12)]">
      <div className="grid gap-0 lg:grid-cols-[0.92fr_1.08fr]">
        <div className="bg-[radial-gradient(circle_at_top_left,_#13213f_0%,_#07111f_100%)] p-6 text-white">
          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">Private career workspace</div>
          <h3 className="mt-3 text-2xl font-semibold">Your career, on your terms.</h3>
          <p className="mt-3 text-sm leading-7 text-slate-300">
            Build a private career profile, explore relevant opportunities and decide when you want to engage.
          </p>
          <div className="mt-6 space-y-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Private profile</div>
              <div className="mt-2 text-lg font-semibold text-white">Visible only within your career workspace</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Introductions</div>
              <div className="mt-2 text-lg font-semibold text-white">Always subject to your permission</div>
            </div>
          </div>
        </div>
        <div className="relative aspect-[4/5] min-h-[340px] overflow-hidden bg-slate-100 lg:aspect-auto">
          <img
            src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=82"
            alt="Professional considering her next career move"
            className="h-full w-full object-cover object-[50%_35%]"
            width="960"
            height="1200"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-transparent" />
        </div>
      </div>
    </div>
  );
}

function CareerAgentPreview() {
  return (
    <div className="min-h-[29rem] overflow-hidden rounded-[2rem] border border-slate-200 bg-[#07111f] p-6 text-white shadow-xl lg:p-8">
      <div className="flex items-center justify-between border-b border-white/10 pb-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#f7911d]">Career Agent</div>
          <div className="mt-1 text-lg font-semibold">Your career workspace</div>
        </div>
        <span className="rounded-full border border-[#f7911d]/30 bg-[#f7911d]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#f7911d]">
          Preview
        </span>
      </div>
      <div className="mt-7 grid gap-4 sm:grid-cols-2">
        {[
          ['Opportunities', 'Roles aligned to your direction'],
          ['Career Goals', 'Priorities and timing'],
          ['Skills Intelligence', 'Capabilities to strengthen'],
          ['Network View', 'Relationships that matter'],
        ].map(([label, value]) => (
          <div key={label} className="min-h-28 rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">{label}</div>
            <div className="mt-3 text-base font-medium leading-6 text-white">{value}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-center justify-between text-xs text-slate-300">
          <span>Career context completeness</span>
          <span>68%</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
          <div className="h-full w-[68%] rounded-full bg-[#f7911d]" />
        </div>
      </div>
    </div>
  );
}

export function ForEmployersPage() {
  const hireHref = '/market-intelligence/briefing?intent=hire-with-us';

  return (
    <div className="min-h-screen bg-[#f7f7f4] text-[#0f172a]">
      <SiteHeader ctaLabel="Hire with us" ctaHref={hireHref} />
      <main>
        <PageHero
          eyebrow="For Employers"
          title="Hire leaders with confidence, speed, and market intelligence."
          description="We help employers understand the market, define the role with precision, and move forward with a shortlist they can trust."
          primaryCta={{ label: 'Hire with us', href: hireHref }}
          secondaryCta={{ label: 'Join the market briefing', href: '/market-intelligence/briefing' }}
          trustItems={employerTrust}
          media={<EmployerHeroMedia />}
        />
        <TrustStrip items={trustItems} />

        <section className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
          <SectionHeader
            eyebrow="Why employers engage"
            title="The cost of poor hiring is bigger than the cost of a search."
            description="Complex markets, scarce leadership talent, and generic search processes create delay and risk. The wrong brief compounds that risk."
          />
          <div className="mt-8">
            <FeatureGrid items={employerProblems} />
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
            <SectionHeader
              eyebrow="Our approach"
              title="A structured combination of search, signal, and advisory support."
              description="We help employers move from a vague requirement to a credible market strategy."
            />
            <div className="mt-8">
              <FeatureGrid items={employerPillars} />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
          <SectionHeader
            eyebrow="Capabilities"
            title="The work is more than sourcing."
            description="These are the practical capabilities that support better search outcomes and better decisions."
          />
          <div className="mt-8">
            <FeatureGrid items={employerCapabilities} />
          </div>
        </section>

        <section className="bg-[#0b1528] text-white">
          <div className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
            <SectionHeader
              eyebrow="Credibility"
              title="Trust is built through outcomes, not adjectives."
              description="The right employer conversation is grounded in evidence, not generic claims."
            />
            <div className="mt-8">
              <ProofGrid items={employerProofStats} />
            </div>
            <div className="mt-10 rounded-[2rem] border border-white/10 bg-white/5 p-8">
              <div className="flex items-start gap-4">
                <MessageSquareQuote className="mt-1 h-6 w-6 shrink-0 text-[#f7911d]" />
                <div>
                  <p className="text-lg leading-8 text-slate-200">
                    &ldquo;The best search partners reduce ambiguity. They help you see the market clearly enough to move with conviction.&rdquo;
                  </p>
                  <div className="mt-4 text-sm text-slate-400">Executive search client, confidential feedback</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
          <SectionHeader
            eyebrow="Process"
            title="A clear sequence from briefing to follow-through."
            description="We reduce uncertainty by showing how the work unfolds and where the signal enters the process."
          />
          <div className="mt-8">
            <Timeline steps={employerProcess} />
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
            <SectionHeader
              eyebrow="FAQ"
              title="Questions employers usually ask."
              align="center"
            />
            <div className="mt-8">
              <FaqAccordion items={employerFaq} />
            </div>
          </div>
        </section>

        <CTASection
          title="Ready to shape the brief?"
          description="Start with a briefing, and we will help you understand the market, the role, and the right path forward."
          primaryCta={{ label: 'Hire with us', href: hireHref }}
          secondaryCta={{ label: 'Join the market briefing', href: '/market-intelligence/briefing' }}
          variant="dark"
        />
      </main>
      <SiteFooter ctaLabel="Hire with us" ctaHref={hireHref} />
    </div>
  );
}

export function ForCandidatesPage() {
  const joinHref = '/market-intelligence/briefing?intent=talent-network';
  const conversationHref = '/market-intelligence/briefing?intent=confidential-career-conversation';

  return (
    <div className="min-h-screen bg-[#f7f7f4] text-[#0f172a]">
      <SiteHeader ctaLabel="Join the talent network" ctaHref={joinHref} />
      <main>
        <PageHero
          eyebrow="Candid-I Talent Network"
          title="Make your next career move with better intelligence."
          description="Join a private talent network for relevant opportunities, market insight and AI-assisted career tools—with you in control of every introduction."
          primaryCta={{ label: 'Join the talent network', href: joinHref }}
          secondaryCta={{ label: 'Explore career tools', href: '#career-agent' }}
          tertiaryCta={{ label: 'Speak with us confidentially', href: conversationHref }}
          trustItems={candidateTrust}
          tone="light"
          media={<CandidateHeroMedia />}
        />
        <TrustStrip items={talentNetworkPrinciples} />

        <section id="career-agent" className="scroll-mt-24 bg-white">
          <div className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-center">
              <div>
                <SectionHeader
                  eyebrow="Your Career Agent"
                  title="One career workspace. Four ways to move forward."
                  description="Organise opportunities, goals, skills and trusted relationships in one evolving workspace."
                />
                <div className="mt-6 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm leading-6 text-slate-700">
                  Career Agent tools are being introduced progressively, with priority access for Talent Network members.
                </div>
              </div>
              <CareerAgentPreview />
            </div>

            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {careerAgentCapabilities.map((capability) => (
                <CareerCapabilityCard key={capability.id} capability={capability} />
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
          <SectionHeader
            eyebrow="The Talent Network"
            title="A career relationship that continues between opportunities."
            description="Keep your career context current so opportunities and intelligence remain relevant over time."
          />
          <div className="mt-8">
            <FeatureGrid items={networkBenefits} />
          </div>
        </section>

        <section className="bg-[#0b1528] text-white">
          <div className="mx-auto grid max-w-[88rem] gap-10 px-5 py-16 lg:grid-cols-[0.8fr_1.2fr] lg:px-8">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">Human-supported</div>
              <h2 className="mt-4 text-3xl font-semibold md:text-4xl">
                AI organises the intelligence. People support the decisions.
              </h2>
              <p className="mt-4 text-base leading-8 text-slate-300">
                Technology supports the work. People remain responsible for important career decisions.
              </p>
            </div>
            <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/5">
              <div className="grid grid-cols-2 border-b border-white/10 px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#f7911d]">
                <span>Career Agent</span>
                <span>Candid-I specialist</span>
              </div>
              {[
                ['Organises opportunities and goals', 'Tests relevance and context'],
                ['Identifies skills and market signals', 'Provides judgement and guidance'],
                ['Maps relevant relationships', 'Supports trusted introductions'],
              ].map(([agent, specialist]) => (
                <div key={agent} className="grid grid-cols-2 gap-4 border-b border-white/10 px-5 py-4 text-sm leading-6 last:border-0">
                  <span className="text-slate-300">{agent}</span>
                  <span className="text-white">{specialist}</span>
                </div>
              ))}
              <p className="border-t border-white/10 px-5 py-4 text-sm font-semibold text-[#f7911d]">
                You remain in control throughout.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
            <SectionHeader
              eyebrow="Opportunities"
              title="Opportunities across specialist markets."
              description="Leadership and specialist appointments where market knowledge and discretion matter."
            />
            <div className="mt-8">
              <FeatureGrid items={opportunityTypes} />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
          <SectionHeader
            eyebrow="How it works"
            title="Move forward at your own pace."
            description="Build career context privately and explore relevant options when the timing is right."
          />
          <div className="mt-8">
            <Timeline steps={candidateProcess} connected />
          </div>
        </section>

        <section className="bg-[#0b1528] text-white">
          <div className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
            <div className="max-w-3xl">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f7911d]">Privacy &amp; control</div>
              <h2 className="mt-4 text-3xl font-semibold md:text-4xl">Your career information remains under your control.</h2>
              <p className="mt-4 text-base leading-8 text-slate-300">
                Your Talent Network profile is private. We explain the context before any introduction and do not share
                your information with an employer without your permission.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {candidateConfidentialityPoints.map((point) => (
                <div key={point} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-5 text-sm leading-6 text-slate-200">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#f7911d]" />
                  {point}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto grid max-w-[88rem] gap-10 px-5 py-16 lg:grid-cols-[0.82fr_1.18fr] lg:items-center lg:px-8">
            <div className="aspect-[3/2] overflow-hidden rounded-[2rem] bg-slate-100">
              <img
                src="https://images.unsplash.com/photo-1551836022-4c4c79ecde51?auto=format&fit=crop&w=1400&q=82"
                alt="Professional career conversation with a trusted adviser"
                className="h-full w-full object-cover"
                width="1200"
                height="800"
                loading="lazy"
              />
            </div>
            <div>
              <SectionHeader
                eyebrow="Ongoing support"
                title="Career intelligence beyond a single role."
                description="Human support helps you interpret the market and navigate important career decisions."
              />
              <div className="mt-8 grid gap-4">
                {candidateSupportItems.map((item) => (
                  <article key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <h3 className="text-base font-semibold text-slate-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">{item.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
          <SectionHeader
            eyebrow="Credibility"
            title="Professional representation starts with trust."
          />
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-[2rem] border border-slate-200 bg-white px-6 py-5 shadow-sm">
            {candidateProofPoints.map((point, index) => (
              <React.Fragment key={point}>
                {index > 0 ? <span className="hidden text-[#f7911d] sm:inline" aria-hidden="true">•</span> : null}
                <span className="text-sm font-semibold text-slate-700">{point}</span>
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-[88rem] px-5 py-16 lg:px-8">
            <SectionHeader eyebrow="FAQ" title="Questions about joining the network." align="center" />
            <div className="mt-8">
              <FaqAccordion items={candidateFaq} />
            </div>
          </div>
        </section>

        <CTASection
          title="Build career intelligence before you need your next move."
          description="Private by default. You decide when an introduction happens."
          primaryCta={{ label: 'Join the talent network', href: joinHref }}
          secondaryCta={{ label: 'Explore career tools', href: '#career-agent' }}
          tertiaryCta={{ label: 'Speak with us confidentially', href: conversationHref }}
          variant="light"
        />
      </main>
      <SiteFooter ctaLabel="Join the talent network" ctaHref={joinHref} />
    </div>
  );
}

export default ForEmployersPage;

